import { useCmsNew } from "@/lib/cms/cms-new-i18n";
import { useAuth } from "@/context/auth/AuthContext";
import { useSections } from "@/api/cms";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  PuckSafeSelect as Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/cms-puck/fields/PuckSafeSelect";

/**
 * Custom field Puck : choisit un découpage PARENT du catalogue raisonné pour le
 * bloc `CatalogueListing`. Valeur = `{ target, decoupageId }`. Charge les
 * découpages de l'instance via `useSections(cmsId, target)` (endpoint BO
 * existant). La création/édition des découpages reste sur la page BO dédiée.
 */

type DecoupageValue = {
  target: "ARTWORKS" | "MULTIPRINTS";
  decoupageId: string | null;
};

export function DecoupagePickerField({
  value,
  onChange,
}: {
  value: DecoupageValue | undefined;
  onChange: (value: DecoupageValue) => void;
}) {
  const cmsNew = useCmsNew();
  const { instanceData } = useAuth();
  const cmsId = instanceData?.CMS?.id ?? "";
  const target: DecoupageValue["target"] = value?.target ?? "ARTWORKS";
  const decoupageId = value?.decoupageId ?? null;

  const { data: sections = [], isLoading } = useSections(cmsId, target);

  const update = (patch: Partial<DecoupageValue>) =>
    onChange({ target, decoupageId, ...patch });

  return (
    <div className="flex flex-col gap-3 p-1">
      <div className="text-xs font-medium text-slate-600">{cmsNew("cms.new.auto.text.decoupage_parent", "Découpage parent")}</div>
      <RadioGroup
        value={target}
        onValueChange={(v) =>
          update({ target: v as DecoupageValue["target"], decoupageId: null })
        }
        className="flex gap-6"
      >
        <div className="flex items-center gap-2">
          <RadioGroupItem value="ARTWORKS" id="dec-artworks" />
          <Label htmlFor="dec-artworks" className="text-sm">
            {cmsNew("cms.new.pickers.decoupage.artworks", "Œuvres")}
          </Label>
        </div>
        <div className="flex items-center gap-2">
          <RadioGroupItem value="MULTIPRINTS" id="dec-multiprints" />
          <Label htmlFor="dec-multiprints" className="text-sm">
            {cmsNew("cms.new.pickers.decoupage.multiprints", "Multiples")}
          </Label>
        </div>
      </RadioGroup>

      {isLoading ? (
        <p className="text-xs text-slate-400">{cmsNew("cms.new.auto.text.chargement_des_decoupages", "Chargement des découpages…")}</p>
      ) : sections.length === 0 ? (
        <p className="text-xs text-slate-400">
          {cmsNew(
            "cms.new.pickers.decoupage.emptyForTarget",
            "Aucun découpage pour cette cible.",
          )}
        </p>
      ) : (
        <Select
          value={decoupageId ?? ""}
          onValueChange={(id) => update({ decoupageId: id })}
        >
          <SelectTrigger className="h-9 bg-white">
            <SelectValue placeholder={cmsNew("cms.new.auto.placeholder.choisir_un_decoupage", "Choisir un découpage…")} />
          </SelectTrigger>
          <SelectContent>
            {sections.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {s.nameMultilingual?.fr ||
                  (s.nameMultilingual
                    ? Object.values(s.nameMultilingual).find(Boolean)
                    : "") ||
                  s.id}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}
